import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import useBreadcrumbs from '../../../utils/hooks/useBreadcrumbs';
import TitlePage from '../../../UI/TitlePage/TitlePage';
import Button from '../../../UI/Button/Button';
import SurveyAPI from '../../../services/api/SurveyService';
import { NotifyContext } from '../../../context/notifyContext';

interface ResponseDetail {
  question: {
    id: number;
    question_text_fr: string;
    question_text_en: string;
    type: 'rating' | 'text' | 'select';
  };
  answer_value: number | null;
  answer_text: string | null;
}

interface SurveyResponseDetails {
  id: number;
  booking: {
    id: number;
    date: string;
    time: string;
    context: string;
  };
  student: {
    id: number;
    name: string;
    email: string;
  };
  tutor: {
    id: number;
    name: string;
    email: string;
  };
  responses: ResponseDetail[];
  submitted_at: string | null;
  created_at: string;
}

const ResponseDetails: React.FC = () => {
  const navigate = useNavigate();
  const notify = useContext(NotifyContext);
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(true);
  const [responseData, setResponseData] = useState<SurveyResponseDetails | null>(null);

  useBreadcrumbs([
    { label: 'Tableau de bord', is_link: true, href: '/app/dashboard' },
    { label: 'Gestion des sondages', is_link: true, href: '/app/surveys' },
    { label: 'Réponses', is_link: true, href: '/app/surveys/responses' },
    { label: 'Détails', is_link: false },
  ]);

  useEffect(() => {
    if (id) {
      fetchResponseDetails();
    }
  }, [id]);

  const fetchResponseDetails = async () => {
    try {
      setLoading(true);
      const response: any = await SurveyAPI.getResponseById(Number(id));
      if (response && response.data) {
        setResponseData(response.data);
      }
    } catch (err: any) {
      notify(err.message || 'Erreur lors du chargement des détails', 'error');
    } finally {
      setLoading(false);
    }
  };

  const renderAnswer = (response: ResponseDetail) => {
    const { question, answer_value, answer_text } = response;

    switch (question.type) {
      case 'rating':
        return (
          <div className="flex items-center gap-2">
            <div className="flex">
              {Array.from({ length: 5 }, (_, index) => {
                const starValue = index + 1;
                const isFilled = answer_value !== null && starValue <= answer_value;
                return (
                  <svg
                    key={starValue}
                    className={`w-6 h-6 ${isFilled ? 'text-yellow-400 fill-current' : 'text-gray-300'}`}
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                );
              })}
            </div>
            <span className="text-gray-600">({answer_value} / 5)</span>
          </div>
        );

      case 'text':
        return (
          <div className="bg-gray-50 p-4 rounded-lg">
            <p className="text-gray-800 whitespace-pre-wrap">{answer_text || '-'}</p>
          </div>
        );

      case 'select':
        return (
          <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
            {answer_text || answer_value || '-'}
          </span>
        );

      default:
        return <span>-</span>;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!responseData) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <p className="text-red-800">Réponse introuvable</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <TitlePage>
        <div className="flex justify-between items-center">
          <span>Détails de la Réponse au Sondage</span>
          <Button
            onClick={() => navigate('/app/surveys/responses')}
            className="bg-gray-200 hover:bg-gray-300"
          >
            Retour à la liste
          </Button>
        </div>
      </TitlePage>

      {/* Booking Information */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-bold text-gray-800 mb-4">Informations sur la Session</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <span className="text-sm text-gray-500">ID Réservation:</span>
            <p className="font-medium">{responseData.booking.id}</p>
          </div>
          <div>
            <span className="text-sm text-gray-500">Date:</span>
            <p className="font-medium">{responseData.booking.date}</p>
          </div>
          <div>
            <span className="text-sm text-gray-500">Heure:</span>
            <p className="font-medium">{responseData.booking.time}</p>
          </div>
          <div>
            <span className="text-sm text-gray-500">Date de soumission:</span>
            <p className="font-medium">
              {responseData.submitted_at
                ? new Date(responseData.submitted_at).toLocaleString('fr-FR')
                : '-'}
            </p>
          </div>
          <div className="md:col-span-2">
            <span className="text-sm text-gray-500">Contexte:</span>
            <p className="font-medium">{responseData.booking.context}</p>
          </div>
        </div>
      </div>

      {/* Student and Tutor Information */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Étudiant</h3>
          <div className="space-y-2">
            <div>
              <span className="text-sm text-gray-500">Nom:</span>
              <p className="font-medium">{responseData.student.name}</p>
            </div>
            <div>
              <span className="text-sm text-gray-500">Email:</span>
              <p className="font-medium">{responseData.student.email}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Tuteur</h3>
          <div className="space-y-2">
            <div>
              <span className="text-sm text-gray-500">Nom:</span>
              <p className="font-medium">{responseData.tutor.name}</p>
            </div>
            <div>
              <span className="text-sm text-gray-500">Email:</span>
              <p className="font-medium">{responseData.tutor.email}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Responses */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-bold text-gray-800 mb-6">Réponses aux Questions</h2>
        <div className="space-y-6">
          {responseData.responses.map((response, index) => (
            <div key={index} className="border-b border-gray-200 pb-6 last:border-0">
              <div className="mb-3">
                <h4 className="font-semibold text-gray-800 mb-1">
                  Question {index + 1} (FR):
                </h4>
                <p className="text-gray-700">{response.question.question_text_fr}</p>
              </div>
              <div className="mb-3">
                <h4 className="font-semibold text-gray-800 mb-1">
                  Question {index + 1} (EN):
                </h4>
                <p className="text-gray-700">{response.question.question_text_en}</p>
              </div>
              <div>
                <span className="text-sm text-gray-500 block mb-2">Réponse:</span>
                {renderAnswer(response)}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ResponseDetails;
